import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import Icon from '../../../components/AppIcon';

const PerformanceChart = () => {
  const [chartType, setChartType] = useState('combined');
  const [timeRange, setTimeRange] = useState('7d');

  const data = [
    {
      date: '08/09',
      fuelConsumption: 245,
      routeEfficiency: 87,
      avgSpeed: 42,
      deliveries: 28,
      fuelCost: 892
    },
    {
      date: '08/10',
      fuelConsumption: 238,
      routeEfficiency: 91,
      avgSpeed: 45,
      deliveries: 32,
      fuelCost: 865
    },
    {
      date: '08/11',
      fuelConsumption: 252,
      routeEfficiency: 84,
      avgSpeed: 38,
      deliveries: 26,
      fuelCost: 918
    },
    {
      date: '08/12',
      fuelConsumption: 241,
      routeEfficiency: 89,
      avgSpeed: 44,
      deliveries: 30,
      fuelCost: 878
    },
    {
      date: '08/13',
      fuelConsumption: 235,
      routeEfficiency: 93,
      avgSpeed: 47,
      deliveries: 35,
      fuelCost: 856
    },
    {
      date: '08/14',
      fuelConsumption: 248,
      routeEfficiency: 86,
      avgSpeed: 41,
      deliveries: 29,
      fuelCost: 903
    },
    {
      date: '08/15',
      fuelConsumption: 243,
      routeEfficiency: 88,
      avgSpeed: 43,
      deliveries: 31,
      fuelCost: 884
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground mb-2">{`Date: ${label}`}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {`${entry?.name}: ${entry?.value}${entry?.name === 'Route Efficiency' ? '%' : entry?.name === 'Fuel Consumption' ? ' gal' : entry?.name === 'Avg Speed' ? ' mph' : entry?.name === 'Fuel Cost' ? ' $' : ''}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (chartType) {
      case 'fuel':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="fuelConsumption" fill="var(--color-primary)" name="Fuel Consumption" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'efficiency':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="routeEfficiency" 
                stroke="var(--color-success)" 
                strokeWidth={3}
                name="Route Efficiency"
                dot={{ fill: 'var(--color-success)', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
              <YAxis yAxisId="left" stroke="var(--color-muted-foreground)" />
              <YAxis yAxisId="right" orientation="right" stroke="var(--color-muted-foreground)" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar yAxisId="left" dataKey="fuelConsumption" fill="var(--color-primary)" name="Fuel Consumption" />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="routeEfficiency" 
                stroke="var(--color-success)" 
                strokeWidth={3}
                name="Route Efficiency"
                dot={{ fill: 'var(--color-success)', strokeWidth: 2, r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Fleet Performance Trends</h3>
        
        <div className="flex items-center space-x-3">
          {/* Time Range Selector */}
          <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setTimeRange('7d')}
              className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
                timeRange === '7d' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              7D
            </button>
            <button
              onClick={() => setTimeRange('30d')}
              className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
                timeRange === '30d' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              30D
            </button>
            <button
              onClick={() => setTimeRange('90d')}
              className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
                timeRange === '90d' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              90D
            </button>
          </div>

          {/* Chart Type Selector */}
          <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setChartType('combined')}
              className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
                chartType === 'combined' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Combined
            </button>
            <button
              onClick={() => setChartType('fuel')}
              className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
                chartType === 'fuel' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Fuel
            </button>
            <button
              onClick={() => setChartType('efficiency')}
              className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
                chartType === 'efficiency' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Efficiency
            </button>
          </div>
        </div>
      </div>
      {/* Chart Container */}
      <div className="p-4">
        {renderChart()}
      </div>
      {/* Summary Stats */}
      <div className="p-4 bg-muted border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Icon name="Fuel" size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">Avg Fuel</span>
            </div>
            <div className="text-lg font-bold text-foreground">
              {(data?.reduce((sum, d) => sum + d?.fuelConsumption, 0) / data?.length)?.toFixed(1)} gal
            </div>
            <div className="text-xs text-muted-foreground">per day</div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Icon name="TrendingUp" size={16} color="var(--color-success)" />
              <span className="text-sm font-medium text-foreground">Efficiency</span>
            </div>
            <div className="text-lg font-bold text-success">
              {(data?.reduce((sum, d) => sum + d?.routeEfficiency, 0) / data?.length)?.toFixed(1)}%
            </div>
            <div className="text-xs text-muted-foreground">average</div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Icon name="Gauge" size={16} color="var(--color-accent)" />
              <span className="text-sm font-medium text-foreground">Avg Speed</span>
            </div>
            <div className="text-lg font-bold text-foreground">
              {(data?.reduce((sum, d) => sum + d?.avgSpeed, 0) / data?.length)?.toFixed(1)} mph
            </div>
            <div className="text-xs text-muted-foreground">fleet wide</div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Icon name="Package" size={16} color="var(--color-warning)" />
              <span className="text-sm font-medium text-foreground">Deliveries</span>
            </div>
            <div className="text-lg font-bold text-foreground">
              {data?.reduce((sum, d) => sum + d?.deliveries, 0)}
            </div>
            <div className="text-xs text-muted-foreground">total this week</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceChart;